/* eslint-disable react-hooks/exhaustive-deps */
import { faArrowRight, faDownload, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Alert, Button } from "react-bootstrap";

import DocFilingApi from "../../../../api/DocFilingApi";
import { appConstants } from "../../../../config/app-constants";
import { EActionItem, EToastvariant } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showToastMsg } from "../../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import FileUploadDropzone from "../../../FileUploadDropzone";
import { ConfirmationModal } from "../../../modals";
import { OverlaySpinner } from "../../../placeholders";
import { UploadedDocument } from "../../assisted";

type Props = {
	handleExtractionSuccess: (responseData: any) => void;
	handleAddManually: () => void;
};

const CGMutualFundsDetailsUploadForm: React.FC<Props> = (props: Props) => {
	const { handleExtractionSuccess, handleAddManually } = props;

	const dispatch = useAppDispatch();

	const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
	const [deleteDocModal, setDeleteDocModal] = useState<any | null>(null);
	const [isAction, setIsAction] = useState<EActionItem>(EActionItem.NULL);
	const [showUploadError, setShowUploadError] = useState<boolean>(false);

	const createSelectedFilesData = async (files: File[]) => {
		try {
			for (let index = 0; index < files.length; index++) {
				const file = files[index];

				const fileData: any = {
					id: `${index}`,
					uuid: `${new Date().getTime()}`,
					fileName: file.name,
					file: file,
					result: null
				};

				setSelectedDocuments((prev) => [...prev, fileData]);
			}
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		}
	};

	const handleDeleteDocument = async () => {
		try {
			if (!deleteDocModal) {
				return;
			}

			setIsAction(EActionItem.DELETE);
			const _selectedDocuments = cloneDeep(selectedDocuments);
			_selectedDocuments.splice(Number(deleteDocModal.id), 1);
			setSelectedDocuments(() => _selectedDocuments);

			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Deleted",
					message: `${deleteDocModal?.fileName} deleted successfully`,
					variant: EToastvariant.Success
				})
			);
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Failed",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setIsAction(EActionItem.NULL);
			setDeleteDocModal(null);
		}
	};

	const handleExtractMutualFundsData = async () => {
		try {
			if (!selectedDocuments?.length) {
				return;
			}

			setIsAction(EActionItem.CREATE);

			const docFilingApi = DocFilingApi.getInstance();
			const formData = new FormData();

			for (let index = 0; index < selectedDocuments.length; index++) {
				const file = selectedDocuments[index].file as File;
				const fileNameHash = selectedDocuments[index].fileNameHash;
				formData.append("mutual_funds", file, fileNameHash);
				const respData = await docFilingApi.extractMutualFundsData(formData);

				if (respData.data.status === 200) {
					handleExtractionSuccess(respData.data.data);
					formData.delete("mutual_funds");
				} else {
					formData.delete("mutual_funds");
					setShowUploadError(() => true);
				}
			}
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setIsAction(EActionItem.NULL);
			setDeleteDocModal(null);
		}
	};

	return (
		<>
			<section className="mb-5">
				<h4 className="fw-bold text-primary mb-4">
					01. Download My Tax Club Template
					<small className="d-block text-greyDark helper-text">
						Fill the details as instructed in the template
					</small>
				</h4>
				<div className="d-inline-block">
					<a
						href={`${appConstants.basePath}/assets/files/mytaxclub-capital-gains-template.xlsx`}
						download="mytaxclub-capital-gains-stock-template"
						target="_blank"
						rel="noreferrer"
						className="btn btn-sm btn-primary btn-w-220 text-white"
					>
						<FASvgIcon icon={faDownload} />
						Download Template
					</a>
				</div>
			</section>
			<section className="mb-5">
				<h4 className="fw-bold text-primary mb-4">
					02. Upload the filled Template
					<small className="d-block text-greyDark helper-text">
						Please upload the filled MyTax Club template below
					</small>
				</h4>
				{selectedDocuments?.map((doc, index) => (
					<UploadedDocument
						key={`mf-details-${index}`}
						uuid={doc.uuid}
						fileName={doc.fileName}
						showDeleteBtn={true}
						handleOnPressDelete={() => setDeleteDocModal(doc)}
						showViewBtn={true}
						handleOnPressView={() => {
							const file = doc.file as Blob;
							const fileUrl = URL.createObjectURL(file);
							const newWindow = window.open(fileUrl, "_blank", "noopener, noreferrer");
							if (newWindow) {
								newWindow.opener = null;
								URL.revokeObjectURL(fileUrl);
							}
						}}
					/>
				))}

				{!selectedDocuments?.length ? (
					<FileUploadDropzone
						title={"Upload the MyTax Club template here"}
						multiple={false}
						allowedFileTypes={[".csv", ".xlsx"]}
						handleOnFileSelect={(files) => createSelectedFilesData(files)}
					/>
				) : null}

				{showUploadError ? (
					<>
						<Alert variant="danger" className="mb-4 size-sm">
							<h5 className="alert-title">
								There was an error processing your document. Please try again or add it manually.
							</h5>
						</Alert>

						<div className="btns-wrap justify-content-between">
							<Button
								variant="link"
								onClick={() => {
									setShowUploadError(() => false);
									handleAddManually();
								}}
								size="sm"
								className="text-primary text-decoration-underline"
							>
								Add Manually
							</Button>
							<Button
								variant="outline-primaryDark"
								onClick={() => {
									setShowUploadError(() => false);
									handleExtractMutualFundsData();
								}}
								size="sm"
							>
								Try Again
								<FASvgIcon icon={faRefresh} />
							</Button>
						</div>
					</>
				) : (
					<div className="btns-wrap mt-5">
						<Button
							variant="primary"
							onClick={() => handleExtractMutualFundsData()}
							disabled={!selectedDocuments?.length || isAction === EActionItem.CREATE}
						>
							Continue
							<FASvgIcon icon={faArrowRight} />
						</Button>
					</div>
				)}
			</section>

			{deleteDocModal && (
				<ConfirmationModal
					showModal={!!deleteDocModal}
					onYesBtnClick={() => handleDeleteDocument()}
					onNoBtnClick={() => setDeleteDocModal(null)}
					title="Please confirm"
					message={`Are you sure you want to delete ${deleteDocModal?.fileName} file?`}
					onYesBtnText="Delete"
					onNoBtnText="Cancel"
					isProcessing={isAction === EActionItem.DELETE}
				/>
			)}

			{isAction === EActionItem.CREATE ? <OverlaySpinner /> : null}
		</>
	);
};

export default CGMutualFundsDetailsUploadForm;
